import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import AiQuoteDialog from "../components/AiQuoteDialog";
import OfferPopup from "../components/OfferPopup";
import { useAuth } from "../context/AuthContext";
import { CATEGORIES, SERVICE_CARDS } from "../lib/api";
import { Sparkles, MapPin, Phone, Mail, ArrowRight, ShieldCheck, Award, Wrench, Flame } from "lucide-react";

const SLUGS = {
  "Climatizzazione": "climatizzazione",
  "Sistemi d'allarme": "sistemi-allarme",
  "Antincendio": "antincendio",
  "Sistemi di sicurezza": "sistemi-sicurezza",
  "Settore elettrico": "settore-elettrico",
  "Automazione": "automazione",
  "Luce e gas": "luce-gas",
  "Fotovoltaico": "fotovoltaico",
  "Idraulica": "idraulica",
};

export default function Landing() {
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInitialPrompt, setAiInitialPrompt] = useState(null);
  const { user } = useAuth();
  const services = CATEGORIES.filter(c => c !== "Altro");
  // Public visitors + logged clienti see offers. Staff/operaio don't.
  const canSeeOffers = !user || user.role === "cliente";
  const openAiFromOffer = (title) => {
    setAiInitialPrompt(`Ciao, mi interessa l'offerta: "${title}". Potete darmi maggiori informazioni?`);
    setAiOpen(true);
  };
  const heroImg = "https://images.unsplash.com/photo-1624397640148-949b1732bb0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
  const chiSiamoImg = "https://images.unsplash.com/photo-1655300256335-beef51a914fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden ew-hero-overlay" data-testid="hero-section">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28 lg:py-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 px-3 py-1 rounded-full text-white text-xs mb-6">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Attivi a Orzinuovi (BS)
            </div>
            <h1 className="heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-none">
              I tuoi impianti,<br/>
              <span className="ew-gradient-text bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">gestiti da chi conosce il mestiere</span>
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              Elettrico, fotovoltaico, sicurezza, automazione, idraulica, climatizzazione, antincendio.<br/>
              Un unico riferimento per tutte le tue esigenze impiantistiche.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => setAiOpen(true)} data-testid="ai-quote-btn"
                      className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-6 text-base rounded-md shadow-xl shadow-amber-500/30">
                <Sparkles className="w-5 h-5 mr-2" /> Richiedi in modo semplice il tuo preventivo
              </Button>
              <Button size="lg" variant="outline" asChild
                      className="bg-white/10 hover:bg-white/20 backdrop-blur border-white/30 text-white px-6 py-6 text-base rounded-md">
                <a href="#servizi">Scopri i servizi <ArrowRight className="w-4 h-4 ml-2" /></a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-center gap-3"><ShieldCheck className="w-6 h-6 text-blue-800"/><div><div className="font-semibold text-slate-900">Certificati</div><div className="text-xs text-slate-500">Personale qualificato</div></div></div>
          <div className="flex items-center gap-3"><Award className="w-6 h-6 text-blue-800"/><div><div className="font-semibold text-slate-900">Partner</div><div className="text-xs text-slate-500">CAME · Daikin · EKO360</div></div></div>
          <div className="flex items-center gap-3"><Wrench className="w-6 h-6 text-blue-800"/><div><div className="font-semibold text-slate-900">9 divisioni</div><div className="text-xs text-slate-500">Impianti completi</div></div></div>
          <div className="flex items-center gap-3"><Sparkles className="w-6 h-6 text-blue-800"/><div><div className="font-semibold text-slate-900">Preventivi IA</div><div className="text-xs text-slate-500">Veloci e semplici</div></div></div>
        </div>
      </section>

      {/* Chi siamo */}
      <section id="chi-siamo" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-semibold uppercase tracking-widest text-blue-800 mb-3">Chi siamo</div>
            <h2 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Un unico punto di riferimento,<br/>tante divisioni di supporto</h2>
            <p className="mt-6 text-slate-700 leading-relaxed">
              Energy World è una realtà affermata nel settore degli impianti tecnologici. La determinazione nel affrontare
              problematiche inerenti al mondo dell'impiantistica con la massima professionalità ci ha permesso di qualificarci come azienda leader.
            </p>
            <p className="mt-4 text-slate-700 leading-relaxed">
              Nel corso del tempo abbiamo creato divisioni interne: elettrica, fotovoltaico, sicurezza, domotica, automazione,
              condizionamento e idraulica, per garantire molteplici tipologie di intervento con qualità e materiali di primo livello.
            </p>
          </div>
          <div className="relative">
            <img src={chiSiamoImg} alt="" className="rounded-lg shadow-2xl object-cover w-full h-96" />
            <div className="absolute -bottom-4 -left-4 bg-blue-800 text-white p-5 rounded-md shadow-xl hidden md:block">
              <div className="heading text-3xl font-extrabold">20+</div>
              <div className="text-xs uppercase tracking-widest">Anni di esperienza</div>
            </div>
          </div>
        </div>
      </section>

      {/* Servizi */}
      <section id="servizi" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <div className="text-sm font-semibold uppercase tracking-widest text-blue-800 mb-3">I servizi</div>
              <h2 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Nove divisioni per il tuo impianto</h2>
            </div>
            <p className="text-slate-600 md:max-w-md mt-4 md:mt-0">Progettazione, installazione e assistenza. Ogni divisione è gestita da tecnici specializzati.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="services-grid">
            {services.map(s => {
              const card = SERVICE_CARDS[s];
              return (
                <Link key={s} to={`/servizi/${SLUGS[s]}`}
                      className="ew-card group relative overflow-hidden rounded-md border border-slate-200 bg-white"
                      data-testid={`service-card-${SLUGS[s]}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    {card.type === "photo" && (
                      <img src={card.url} alt={s} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                    {card.type === "brand" && (
                      <div className={`w-full h-full bg-gradient-to-br ${card.bg} flex flex-col items-center justify-center text-white p-6 text-center`}>
                        <div className="heading text-4xl md:text-5xl font-extrabold tracking-tight">{card.brand}</div>
                        <div className="mt-2 text-xs uppercase tracking-widest opacity-80">{card.subtitle}</div>
                        <div className="mt-4 text-xs opacity-90">{card.tag}</div>
                      </div>
                    )}
                    {card.type === "logo" && (
                      <div className="w-full h-full bg-white flex flex-col items-center justify-center p-8 text-center">
                        <img src={card.url} alt={s} className="max-h-32 max-w-[80%] object-contain group-hover:scale-105 transition-transform duration-500"/>
                        <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{card.subtitle}</div>
                        <div className="mt-1 text-xs text-slate-600">{card.tag}</div>
                      </div>
                    )}
                    {card.type === "icon" && (
                      <div className={`w-full h-full bg-gradient-to-br ${card.bg} flex flex-col items-center justify-center text-white p-6 text-center relative overflow-hidden`}>
                        <div className="absolute inset-0 ew-noise opacity-30"/>
                        <Flame className="w-20 h-20 relative z-10 drop-shadow-lg" strokeWidth={1.5}/>
                        <div className="mt-3 text-xs uppercase tracking-widest opacity-90 relative z-10">{card.subtitle}</div>
                        <div className="mt-2 text-xs opacity-80 relative z-10">{card.tag}</div>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="heading font-bold text-lg text-slate-900">{s}</h3>
                    <div className="mt-2 flex items-center text-blue-800 text-sm font-medium">
                      Scopri di più <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"/>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-800 relative overflow-hidden">
        <div className="absolute inset-0 ew-noise opacity-30" />
        <div className="relative max-w-4xl mx-auto text-center px-6">
          <h2 className="heading text-3xl md:text-5xl font-extrabold text-white tracking-tight">Non sai da dove iniziare?</h2>
          <p className="mt-4 text-blue-100 text-lg max-w-2xl mx-auto">
            Descrivi la tua esigenza al nostro assistente IA. Ti farà 2-3 domande e prepareremo il preventivo per te.
          </p>
          <Button size="lg" onClick={() => setAiOpen(true)} data-testid="ai-quote-btn-cta"
                  className="mt-8 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-6 text-base rounded-md shadow-xl">
            <Sparkles className="w-5 h-5 mr-2" /> Richiedi in modo semplice
          </Button>
        </div>
      </section>

      {/* Contatti */}
      <section id="contatti" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12">
          <div>
            <div className="text-sm font-semibold uppercase tracking-widest text-blue-800 mb-3">Dove trovarci</div>
            <h2 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">Vieni a trovarci</h2>
            <div className="mt-8 space-y-4 text-slate-700">
              <div className="flex items-start gap-3"><MapPin className="w-5 h-5 text-blue-800 mt-1"/><div><div className="font-semibold">Sede operativa</div>Via Convento aguzzano 5<br/>25034 Orzinuovi (BS)</div></div>
              <div className="flex items-start gap-3"><Phone className="w-5 h-5 text-blue-800 mt-1"/><div><div className="font-semibold">Telefono</div>030 9444899</div></div>
              <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-blue-800 mt-1"/><div><div className="font-semibold">Email</div>info@energyworld.store</div></div>
            </div>
          </div>
          <div className="rounded-md overflow-hidden shadow-xl border border-slate-200">
            <iframe title="mappa" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d680.3532005988527!2d9.919887810421711!3d45.40618836459558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478113c2169ceb9d%3A0x27e92e2ebcf298b9!2sEnergy%20World!5e0!3m2!1sit!2sit!4v1746700934406!5m2!1sit!2sit"
                    width="100%" height="400" style={{border:0}} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-slate-400 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="Energy World" className="w-10 h-10 rounded-full bg-white p-1"/>
            <div className="text-sm">© {new Date().getFullYear()} Energy World S.r.l.</div>
          </div>
          <div className="text-sm">Orzinuovi (BS) · info@energyworld.store · 030 9444899</div>
        </div>
      </footer>

      <AiQuoteDialog open={aiOpen} onOpenChange={(v) => { setAiOpen(v); if (!v) setAiInitialPrompt(null); }} initialPrompt={aiInitialPrompt} />
      {canSeeOffers && <OfferPopup onRequestInfo={openAiFromOffer}/>}
    </div>
  );
}
